import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { MetadataApiService } from '../metadata-api/metadata-api.service';
import { QueryApiService } from '../customer-api/query-api.service';
import { EntityRequestApiService } from '../customer-api/entity-request-api.service';
import { QueryParamsService } from 'src/app/shared/utils/query-params.service';
import { writeSearchHandoff } from 'src/app/shared/utils/search-handoff';
import {
  CommodityQueryRequest,
  CompanyFormData,
  CompanyQueryRequest,
  EntityEnrichmentRequest,
  EntityEnrichmentResponse,
  LexiconResponse,
  TopicQueryRequest,
  YukkaQuery,
} from '../../models/ai-search.interface';

export interface AiGeneratedQueryData {
  name: string;
  description: string;
  query: YukkaQuery;
  groups: unknown[];
  _isAiGenerated: true;
}

// Enrichment runs a battery of web research and company generation verifies
// every query branch against the corpus — both take tens of seconds. Long
// ceiling, but never an indefinite spinner.
const GENERATE_TIMEOUT_MS = 180_000;

export function truncateDescription(description: string): string {
  if (!description || description.length <= 200) {
    return description;
  }
  const lastDot = description.lastIndexOf('.', 200);
  if (lastDot !== -1) {
    return description.slice(0, lastDot + 1);
  }
  return description.slice(0, 200);
}

@Injectable({ providedIn: 'root' })
export class AiSearchService {
  pendingLegalName: string | null = null;
  lastGeneratedQueryData: AiGeneratedQueryData | null = null;

  constructor(
    private metadataApi: MetadataApiService,
    private queryParamsService: QueryParamsService,
    private queryApi: QueryApiService,
    private entityRequestApi: EntityRequestApiService,
    private router: Router,
  ) {}

  // Sets the in-memory search context (query name + payload) that the search
  // chart, report-box, and Edit-search dialog read. Does NOT touch
  // localStorage — inline consumers (the DD page) must not overwrite the
  // user's persisted "last search" slot just by viewing results; the write
  // happens in launchAiSearch, right before actually navigating there.
  applySearchState(data: AiGeneratedQueryData): void {
    this.lastGeneratedQueryData = data;
    this.queryApi.query = {
      type: 'search',
      name: data.name,
      description: data.description,
      query: data.query,
    };
  }

  // Mirrors the AI branch of QueryEditorComponent.iSearch().
  launchAiSearch(
    data: AiGeneratedQueryData,
    options?: { time?: string },
  ): void {
    this.applySearchState(data);
    // Transient handoff for search.guard on the search route (survives a reload
    // of that tab). sessionStorage, not persisted storage — the guard bounces to
    // home if it is missing, so a blocked store degrades gracefully.
    writeSearchHandoff(this.queryApi.query);
    this.router.navigate(['/cockpit/news/search/chart'], {
      queryParams: {
        id: Math.random(),
        portfolioId: null,
        groupId: null,
        type: null,
        eventid: null,
        eventype: null,
        scoreevent: null,
        newsaudit: null,
        tag: null,
        participant: null,
        score: null,
        time: options?.time ?? '180',
      },
      queryParamsHandling: 'merge',
    });
  }

  // Navigate to an existing entity's market chart, clearing leftover
  // entity/event query params. Shared by the AI-search dialog and the
  // due-diligence page (both bypass query generation for known entities).
  navigateToEntity(entity: { type: string; id: string }): void {
    this.router.navigate(['/cockpit/news/market/chart'], {
      queryParams: {
        type: entity.type,
        id: entity.id,
        eventid: null,
        eventype: null,
        scoreevent: null,
        tag: null,
        participant: null,
        newstype: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  // Company generation + the fire-and-forget entity request that always
  // accompanies it (backend team gets notified about unknown companies).
  generateCompanySearch(data: CompanyFormData): Observable<LexiconResponse> {
    this.entityRequestApi.addRequestEntity(data.entityRequestData).subscribe({
      error: (err) => console.error('Entity request failed:', err),
    });
    return this.generateCompanyQuery(data.request);
  }

  getLanguages(): string[] {
    const lang = this.queryParamsService.queryParams?.lang;
    if (lang && lang !== 'multi') {
      return lang.split(',');
    }
    return ['en'];
  }

  generateCompanyQuery(
    request: CompanyQueryRequest,
  ): Observable<LexiconResponse> {
    return this.metadataApi
      .post<LexiconResponse>('generate/company', request)
      .pipe(timeout(GENERATE_TIMEOUT_MS));
  }

  generateTopicQuery(request: TopicQueryRequest): Observable<LexiconResponse> {
    return this.metadataApi
      .post<LexiconResponse>('generate/topic', request)
      .pipe(timeout(GENERATE_TIMEOUT_MS));
  }

  generateCommodityQuery(
    request: CommodityQueryRequest,
  ): Observable<LexiconResponse> {
    return this.metadataApi
      .post<LexiconResponse>('generate/commodity', request)
      .pipe(timeout(GENERATE_TIMEOUT_MS));
  }

  generateEntityEnrichment(
    request: EntityEnrichmentRequest,
  ): Observable<EntityEnrichmentResponse> {
    return this.metadataApi
      .post<EntityEnrichmentResponse>('generate/entity-enrichment', request)
      .pipe(timeout(GENERATE_TIMEOUT_MS));
  }
}
